import React, { useCallback, useEffect, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import CommomButton from "../../../components/common-button/index";
import axiosapi from "../../../utilities/axiosApi";
import DeletePopup from "../../../components/delete-popup/index";
import {
  Copy02Icon,
  ReloadIcon,
  Cancel01Icon,
  UserGroup03Icon,
  Link01Icon,
  SquareLock01Icon,
  WarningIcon,
  CancelCircleIcon,
  CheckmarkCircle04Icon,
  Delete02Icon,
} from "../../../icons/icons";

/**
 * Share link for manual-fulfilment campaigns.
 *
 * The customer hands the URL and the 6-digit code to their helpers, who open it
 * on their phones without logging in. Both stay readable here on purpose - the
 * code is meant to be read out over the phone whenever someone needs it.
 *
 * The roster lists devices, not people. A browser cannot see hardware, so the
 * same helper on a phone, in WhatsApp's in-app browser and on a laptop is three
 * rows; each carries what tells them apart instead of pretending otherwise.
 *
 * `basePath` lets the same card serve the customer, reseller and admin panels,
 * which mount the identical endpoints under their own prefixes.
 */
export default function ShareLinkCard({ clientEventId, basePath = "/api/customer" }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  // The action a confirm dialog is currently asking about.
  const [pending, setPending] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchLink = useCallback(async () => {
    if (!clientEventId) return;

    try {
      const res = await axiosapi.get(`${basePath}/share-link/${clientEventId}`);
      if (res.data?.success) {
        setLink(res.data.data);
        setError("");
      } else {
        setError(res.data?.message || "Could not load the share link.");
      }
    } catch (e) {
      setError("Could not load the share link.");
    } finally {
      setLoading(false);
    }
  }, [clientEventId, basePath]);

  useEffect(() => {
    fetchLink();
  }, [fetchLink]);

  const act = async (path, key, confirmText) => {
    if (confirmText) {
      setPending({ path, key, ...confirmText });
      return;
    }
    await run(path, key);
  };

  const run = async (path, key) => {
    setPending(null);
    setBusy(key);
    try {
      const res = await axiosapi.post(`${basePath}/share-link/${clientEventId}/${path}`);
      if (res.data?.success) {
        await fetchLink();
      } else {
        setError(res.data?.message || "That didn't work.");
      }
    } catch (e) {
      setError("That didn't work.");
    } finally {
      setBusy("");
    }
  };

  const copy = async (value, key) => {
    try {
      // Requires a secure context; falls back to a selectable prompt so the
      // helper is never left with no way to get the value.
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      window.prompt("Copy this:", value);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2 border-0 common-shadow w-100 common-card">
        <Card.Body className="p-0 common-card-body text-center py-4">
          <Spinner animation="border" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  const revoked = link && (!link.is_active || link.revoked_at);
  const devices = link?.devices || [];
  const active = devices.filter((d) => !d.is_stale);
  const inactive = devices.filter((d) => d.is_stale);
  const slotsFull = link && link.active_device_count >= link.max_devices;

  const renderDevice = (d) => {
    const key = `dev-${d.id}`;
    const working = busy === key;

    return (
      <li key={d.id} className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom">
        <div className="min-w-0 flex-grow-1">
          <div className={`fw-medium text-truncate ${d.is_blocked ? "text-muted" : ""}`}>
            {d.name}
            {d.is_blocked && (
              <span className="badge rounded-pill bg-secondary fw-normal ms-2 align-middle">
                Blocked
              </span>
            )}
          </div>
          <small className="text-muted d-flex flex-wrap gap-2">
            <span>{d.device_label}</span>
            <span>{d.last_seen_at ? `active ${timeAgo(d.last_seen_at)}` : "not yet active"}</span>
            {d.sent_count > 0 && <span>{d.sent_count} sent</span>}
            {d.called_count > 0 && <span>{d.called_count} called</span>}
          </small>
        </div>

        <div className="d-flex align-items-center gap-1 flex-shrink-0">
          <CommomButton
            onClick={() => act(`devices/${d.id}/block`, key)}
            icon={
              d.is_blocked ? (
                <CheckmarkCircle04Icon width={16} height={16} color="#444050" strokeWidth="2" />
              ) : (
                <CancelCircleIcon width={16} height={16} color="#444050" strokeWidth="2" />
              )
            }
            title={d.is_blocked ? "Unblock" : "Block"}
            disabled={working}
            className="btn-outline-primary btn-sm"
          />
          {/* Forgetting a device is how test rows and old phones leave the
              list. Their history stays: actions are recorded under the name. */}
          <CommomButton
            onClick={() =>
              act(`devices/${d.id}/remove`, key, {
                title: `Remove ${d.name}?`,
                content:
                  "This device is signed out and leaves the list. Anything they already marked stays recorded under their name, and they can join again with the link and code if there is room.",
                saveTitle: "Remove helper",
                icon: <Delete02Icon width={40} height={40} color="#EA5455" strokeWidth="1.6" />,
              })
            }
            icon={<Delete02Icon width={16} height={16} color="#EA5455" strokeWidth="2" />}
            disabled={working}
            className="btn-outline-danger btn-sm px-2"
          />
        </div>
      </li>
    );
  };

  return (
    <Card className="rounded-2 border-0 common-shadow w-100 common-card">
      <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
        <div>
          <h5 className="fw-medium text-capitalize mb-1">Helper Share Link</h5>
          <p className="fw-medium mb-0">
            {revoked
              ? "Revoked - generate a new URL to share again"
              : "Send this to anyone helping invite guests. No login needed."}
          </p>
        </div>
      </Card.Header>

      <Card.Body className="p-0 common-card-body">
        {error && (
          <p className="text-danger small mb-3 d-flex align-items-center gap-2">
            <WarningIcon width={16} height={16} />
            {error}
          </p>
        )}

        {link && !revoked && (
          <>
            {/* Copying the link is what this card is for; it is the one solid
                button. Everything else is quieter. */}
            <div className="mb-3">
              <label className="fw-medium d-flex align-items-center gap-2 mb-1">
                <Link01Icon width={18} height={18} color="#444050" strokeWidth="1.8" />
                Link
              </label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <code
                  className="flex-grow-1 text-truncate p-2 rounded-1 bg-light"
                  style={{ minWidth: 0 }}
                  title={link.url}
                >
                  {link.url}
                </code>
                <CommomButton
                  onClick={() => copy(link.url, "url")}
                  icon={<Copy02Icon width={16} height={16} color="#ffffff" strokeWidth="2" />}
                  title={copied === "url" ? "Copied" : "Copy link"}
                  className="btn-primary"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="fw-medium d-flex align-items-center gap-2 mb-1">
                <SquareLock01Icon width={18} height={18} color="#444050" strokeWidth="1.8" />
                Access code
              </label>
              <div className="d-flex align-items-center gap-2">
                <code className="flex-grow-1 p-2 rounded-1 bg-light fs-5 letter-spacing-1">
                  {link.otp}
                </code>
                <CommomButton
                  onClick={() => copy(link.otp, "otp")}
                  icon={<Copy02Icon width={16} height={16} color="#444050" strokeWidth="2" />}
                  title={copied === "otp" ? "Copied" : "Copy"}
                  className="btn-outline-primary"
                />
              </div>
              <small className="text-muted">
                This code does not change. Anyone with the link and the code can see this
                event's guest list.
              </small>
            </div>

            <div className="mb-4">
              <label className="fw-medium d-flex align-items-center gap-2 mb-1">
                <UserGroup03Icon width={18} height={18} color="#444050" strokeWidth="1.8" />
                Helpers
                <span className="text-muted fw-normal">
                  {link.active_device_count} of {link.max_devices} slots used
                </span>
              </label>

              {slotsFull && (
                <p className="text-warning-emphasis small mb-2 d-flex align-items-center gap-2">
                  <WarningIcon width={16} height={16} />
                  Every slot is in use. Remove or block a helper before someone new can join.
                </p>
              )}

              {devices.length === 0 ? (
                <small className="text-muted d-block">
                  No one has opened the link yet. Helpers appear here as they join.
                </small>
              ) : (
                <ul className="list-unstyled mb-0">{active.map(renderDevice)}</ul>
              )}

              {inactive.length > 0 && (
                <>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mt-2 text-muted text-decoration-none"
                    onClick={() => setShowInactive((v) => !v)}
                  >
                    {showInactive ? "Hide" : "Show"} {inactive.length} inactive (not seen in 30 days)
                  </button>
                  {showInactive && (
                    <ul className="list-unstyled mb-0 mt-1 opacity-75">{inactive.map(renderDevice)}</ul>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Rotating either value signs everyone out; revoking ends the link.
            None of these is the common action, so none of them is solid. */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <CommomButton
            onClick={() =>
              act("rotate-otp", "otp", {
                title: "Generate a new access code?",
                content:
                  "The current code stops working immediately, and every helper has to be given the new one.",
                saveTitle: "Generate new code",
                icon: <SquareLock01Icon width={40} height={40} color="#EA5455" strokeWidth="1.6" />,
              })
            }
            icon={<ReloadIcon width={16} height={16} color="#444050" strokeWidth="2" />}
            title={busy === "otp" ? "Working..." : "New code"}
            disabled={!!busy}
            className="btn-outline-primary"
          />
          <CommomButton
            onClick={() =>
              act("rotate-token", "token", {
                title: "Generate a new URL?",
                content:
                  "The current link stops working immediately. Anyone already using it is signed out and needs the new URL.",
                saveTitle: "Generate new URL",
                icon: <Link01Icon width={40} height={40} color="#EA5455" strokeWidth="1.6" />,
              })
            }
            icon={<ReloadIcon width={16} height={16} color="#444050" strokeWidth="2" />}
            title={busy === "token" ? "Working..." : "New URL"}
            disabled={!!busy}
            className="btn-outline-primary"
          />
          {!revoked && (
            <CommomButton
              onClick={() =>
                act("revoke", "revoke", {
                  title: "Revoke this link?",
                  content:
                    "Everyone currently using it is signed out straight away. You can generate a new URL afterwards.",
                  saveTitle: "Revoke link",
                  icon: <Cancel01Icon width={40} height={40} color="#EA5455" strokeWidth="1.6" />,
                })
              }
              icon={<Cancel01Icon width={16} height={16} color="#EA5455" strokeWidth="2" />}
              title={busy === "revoke" ? "Working..." : "Revoke"}
              disabled={!!busy}
              className="btn-outline-danger ms-auto"
            />
          )}
        </div>
      </Card.Body>

      <DeletePopup
        show={!!pending}
        onHide={() => setPending(null)}
        icon={pending?.icon}
        title={pending?.title}
        content={pending?.content}
        removeTitle="Keep it"
        saveTitle={pending?.saveTitle}
        disabled={!!busy}
        onClickSave={() => run(pending.path, pending.key)}
      />
    </Card>
  );
}

/** "just now", "5 min ago", "3 hours ago", "yesterday", "12 days ago". */
function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "yesterday";
  return `${d} days ago`;
}
